import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { db } from '../../firebase';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import '../../styles/UserProfile.css';

// Function to generate default avatar URL based on user name
const getDefaultAvatar = (name) => {
  const encodedName = encodeURIComponent(name || 'User');
  return `https://ui-avatars.com/api/?name=${encodedName}&background=ff4d4d&color=fff&size=128`;
};

const UserProfile = ({ user }) => {
  const [displayName, setDisplayName] = useState(user?.displayName || user?.email?.split('@')[0] || 'User');
  const [profileData, setProfileData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [avatar, setAvatar] = useState(user?.photoURL || getDefaultAvatar(user?.displayName || user?.email?.split('@')[0]));
  const [avatarFile, setAvatarFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      
      try {
        setError('');
        const userDocRef = doc(db, 'userProfiles', user.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          setProfileData(userDoc.data());
          // Update local displayName with the one from Firestore if it exists
          if (userDoc.data().displayName) {
            setDisplayName(userDoc.data().displayName);
          }
        } else {
          // Create default profile if it doesn't exist (Phase 2 schema)
          const defaultProfile = {
            displayName: user.displayName || user.email.split('@')[0],
            email: user.email,
            role: 'community',
            roleVerified: false,
            casesCreated: 0,
            evidenceSubmitted: 0,
            ordersCreated: 0,
        jobsCompleted: 0,
            totalSpent: 0,
            joinDate: serverTimestamp(),
            bio: '',
            location: '',
            budgetLimits: {
              daily: 10.00,
              monthly: 100.00,
              perAnalysis: 5.00
            },
            collaborationSettings: {
              allowDirectMessages: false,
              shareAnalysisHistory: false,
              expertConsultationAvailable: false
            }
          };
          setProfileData(defaultProfile);
          
          try {
            // Create the user profile document with merge option
            await setDoc(userDocRef, defaultProfile, { merge: true });
            console.log('Created default profile');
          } catch (createError) {
            console.error('Error creating default profile:', createError);
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setError('Failed to load profile data. Please try again.');
        
        // Set default profile data if fetch fails (Phase 2 schema)
        setProfileData({
          displayName: user.displayName || user.email.split('@')[0],
          email: user.email,
          role: 'community',
          roleVerified: false,
          casesCreated: 0,
          evidenceSubmitted: 0,
          ordersCreated: 0,
        jobsCompleted: 0,
          totalSpent: 0,
          joinDate: serverTimestamp(),
          bio: '',
          location: '',
          budgetLimits: {
            daily: 10.00,
            monthly: 100.00,
            perAnalysis: 5.00
          },
          collaborationSettings: {
            allowDirectMessages: false,
            shareAnalysisHistory: false,
            expertConsultationAvailable: false
          }
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [user]);
  
  const handleAvatarChange = (e) => {
    if (e.target.files[0]) {
      setAvatarFile(e.target.files[0]);
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader.result);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };
  
  const handleSaveProfile = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError('');
      setUpdateSuccess(false);
      
      const userDocRef = doc(db, 'userProfiles', user.uid);
      
      // Upload avatar if new one is selected
      let photoURL = user.photoURL;
      if (avatarFile) {
        try {
          const storage = getStorage();
          const avatarRef = ref(storage, `avatars/${user.uid}`);
          await uploadBytes(avatarRef, avatarFile);
          photoURL = await getDownloadURL(avatarRef);
          console.log('Avatar uploaded successfully:', photoURL);
        } catch (uploadError) {
          console.error('Error uploading avatar:', uploadError);
          throw new Error(`Avatar upload failed: ${uploadError.message}`);
        }
      }
      
      // Prepare updated profile data
      const updatedProfileData = {
        ...profileData,
        displayName: displayName // Make sure displayName is included
      };
      
      // Update Firestore profile with all fields including displayName using merge option
      try {
        await setDoc(userDocRef, updatedProfileData, { merge: true });
      } catch (updateError) {
        console.error('Error updating Firestore profile:', updateError);
        throw new Error('Failed to update profile in database');
      }
      
      // Update Auth profile
      try {
        await updateProfile(user, {
          displayName: displayName,
          photoURL: photoURL
        });
      } catch (authUpdateError) {
        console.error('Error updating Auth profile:', authUpdateError);
        throw new Error('Failed to update display name');
      }
      
      // Show success message
      setUpdateSuccess(true);
      
      // Force refresh of the user object in 1 second
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading && !profileData) {
    return <div className="loading">Loading profile...</div>;
  }
  
  return (
    <div className="user-profile">
      {error && <div className="profile-error">{error}</div>}
      {updateSuccess && <div className="profile-success">Profile updated successfully! Refreshing...</div>}
      
      <div className="profile-header">
        <div className="avatar-container">
          <img src={avatar} alt="User avatar" className="avatar" />
          {isEditing && (
            <div className="avatar-upload">
              <label htmlFor="avatar-input" className="avatar-edit-button">
                Change
              </label>
              <input
                id="avatar-input"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </div>
        
        <div className="user-info">
          {isEditing ? (
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="display-name-input"
              placeholder="Display Name"
            />
          ) : (
            <h2>{displayName || user.displayName || 'User'}</h2>
          )}
          <p className="user-email">{user.email}</p>
          <p className="user-role">{profileData?.role || 'community'}</p>
        </div>
      </div>
      
      <div className="profile-stats">
        <div className="stat-item">
          <div className="stat-value">{profileData?.casesCreated || 0}</div>
          <div className="stat-label">Cases Created</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{profileData?.evidenceSubmitted || 0}</div>
          <div className="stat-label">Evidence Submitted</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{
            profileData?.joinDate 
              ? new Date(profileData.joinDate).toLocaleDateString() 
              : new Date().toLocaleDateString()
          }</div>
          <div className="stat-label">Join Date</div>
        </div>
      </div>
      
      {isEditing && (
        <div className="profile-edit">
          <div className="form-group">
            <label>Bio</label>
            <textarea 
              value={profileData?.bio || ''} 
              onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
              placeholder="Tell us about yourself"
            />
          </div>
          
          <div className="form-group">
            <label>Location</label>
            <input 
              type="text" 
              value={profileData?.location || ''} 
              onChange={(e) => setProfileData({...profileData, location: e.target.value})}
              placeholder="City, Country"
            />
          </div>
        </div>
      )}
      
      <div className="profile-actions">
        {isEditing ? (
          <>
            <button className="save-button" onClick={handleSaveProfile} disabled={loading}>
              {loading ? 'Saving...' : 'Save Profile'}
            </button>
            <button className="cancel-button" onClick={() => setIsEditing(false)} disabled={loading}>Cancel</button>
          </>
        ) : (
          <button className="edit-button" onClick={() => setIsEditing(true)}>Edit Profile</button>
        )}
      </div>
    </div>
  );
};

export default UserProfile; 